import { Activity, MapPin } from 'lucide-react';
import type { CockpitActivityPulse } from '../../types';

const TYPE_LABELS: Array<{ key: keyof CockpitActivityPulse['byType']; label: string }> = [
  { key: 'CALL', label: 'Calls' },
  { key: 'TEXT', label: 'Texts' },
  { key: 'EMAIL', label: 'Emails' },
  { key: 'IN_PERSON', label: 'In-person' },
  { key: 'NOTE', label: 'Notes' },
  { key: 'OTHER', label: 'Other' },
];

/** "Jul 1 – Jul 29, 2026" from the period the activity was actually counted over. */
function periodCaption(period?: { from: string; to: string }): string {
  if (!period) return '';
  const opts = { month: 'short', day: 'numeric', timeZone: 'UTC' } as const;
  const from = new Date(period.from).toLocaleDateString('en-US', opts);
  const to = new Date(period.to).toLocaleDateString('en-US', { ...opts, year: 'numeric' });
  return `${from} – ${to}`;
}

export function ActivityPulse({ data, period }: {
  data: CockpitActivityPulse; period?: { from: string; to: string };
}) {
  // CRM-121: activity is counted over its OWN window, which is not the fundraising
  // window beside it — a season is a set of teams, not a date range, so the two
  // cannot be made to match. Say what this one is rather than let a reader assume.
  const caption = periodCaption(period);
  return (
    <div className="card p-4 sm:p-6">
      <div className="flex items-center gap-2 mb-1">
        <Activity className="w-4 h-4 text-blast-600 dark:text-blast-400" />
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Activity</h3>
        <span className="ml-auto text-2xl font-bold tabular-nums text-gray-900 dark:text-gray-100">{data.total.toLocaleString()}</span>
      </div>
      {caption && <div className="mb-3 text-[11px] text-gray-500 dark:text-gray-400">{caption}</div>}

      {data.total === 0 ? (
        <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-6">No activity logged in this period yet.</p>
      ) : (
        <div className="grid grid-cols-2 gap-2 text-center sm:grid-cols-3">
          {TYPE_LABELS.map((t) => (
            <div key={t.key} className="rounded-lg bg-gray-50 dark:bg-gray-700/40 py-2">
              <div className="text-lg font-semibold tabular-nums text-gray-900 dark:text-gray-100">{data.byType[t.key]}</div>
              <div className="text-[11px] text-gray-500 dark:text-gray-400">{t.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Onsite (only meaningful once there's activity) */}
      {data.total > 0 && (
        <div className="mt-4 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 border-t border-gray-100 dark:border-gray-700 pt-3">
          <MapPin className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
          <span className="font-semibold tabular-nums">{data.onsite}</span>
          <span>onsite visits</span>
          <span className="text-gray-400 dark:text-gray-500">· {data.onsiteWithGps} GPS-verified</span>
        </div>
      )}
    </div>
  );
}
